import { memo, useCallback, useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Flex, Skeleton, Typography } from 'antd';
import cn from 'classnames';

import { Card } from '@/shared/ui/card';
import { Sparkline } from '@/shared/ui/sparkline';

import { parseNegativeNumber } from '../../lib/parse-negative-number';
import { useGetTradingPlatformSessionCurrency } from '../../models/selectors/get-trading-platform-session-currency';
import { TradingPlatformStatisticPieChart } from '../trading-platform-statistic-pie-chart/trading-platform-statistic-pie-chart';

import cls from './trading-platform-statistic-card.module.css';

type SparklineColor = 'positive' | 'negative' | 'neutral' | 'auto';

interface MetricItem {
    hasColor?: boolean;
    hasPrefix?: boolean;
    preferredColor?: 'up' | 'down';
    titleKey: string;
    value: string;
}

type SkeletonVariant = 'metric-rings' | 'ratio-bar';

interface TradingPlatformStatisticCardCommon {
    helpId?: string;
    isLoading: boolean;
    preferredColor?: 'up' | 'down' | undefined;
    skeletonVariant?: SkeletonVariant;
    sparklineColor?: SparklineColor;
    sparklineData?: number[];
    subtitle?: string;
    titleKey: string;
    visualIndicator?: ReactNode;
}

interface TradingPlatformStatisticCardAsValueProps
    extends TradingPlatformStatisticCardCommon {
    hasColor?: boolean;
    hasPrefix?: boolean;
    metrics?: never;
    percentage?: string;
    /**
     * Optional second value rendered underneath the main one, with its own label.
     * Used by the balance card to show the reconstructed balance below the real
     * broker balance: the two are different figures, not two renderings of one.
     */
    secondaryLabel?: string | undefined;
    secondaryValue?: string | undefined;
    value: string;
    winLoss?: never;
    winPercent?: never;
}

interface TradingPlatformStatisticCardAsPieChartProps
    extends TradingPlatformStatisticCardCommon {
    hasColor?: never;
    hasPrefix?: never;
    metrics?: never;
    secondaryLabel?: never;
    secondaryValue?: never;
    value?: never;
    winLoss?: [number, number] | undefined;
    winPercent: number;
}

interface TradingPlatformStatisticCardAsMultiMetricsProps
    extends TradingPlatformStatisticCardCommon {
    hasColor?: never;
    hasPrefix?: never;
    metrics: MetricItem[];
    secondaryLabel?: never;
    secondaryValue?: never;
    value?: never;
    winLoss?: never;
    winPercent?: never;
}

type TradingPlatformStatisticCardProps =
    | TradingPlatformStatisticCardAsValueProps
    | TradingPlatformStatisticCardAsPieChartProps
    | TradingPlatformStatisticCardAsMultiMetricsProps;

export const TradingPlatformStatisticCard = memo(
    (props: TradingPlatformStatisticCardProps) => {
        const {
            hasColor = false,
            hasPrefix,
            helpId,
            isLoading,
            preferredColor,
            skeletonVariant = 'metric-rings',
            sparklineColor = 'auto',
            sparklineData,
            subtitle,
            titleKey,
            visualIndicator,
        } = props;
        const metrics =
            'metrics' in props
                ? (props as TradingPlatformStatisticCardAsMultiMetricsProps)
                      .metrics
                : undefined;
        const percentage =
            'percentage' in props
                ? (props as TradingPlatformStatisticCardAsValueProps).percentage
                : undefined;
        const value =
            'value' in props
                ? (props as TradingPlatformStatisticCardAsValueProps).value
                : undefined;
        const winLoss =
            'winLoss' in props
                ? (props as TradingPlatformStatisticCardAsPieChartProps).winLoss
                : undefined;
        const winPercent =
            'winPercent' in props
                ? (props as TradingPlatformStatisticCardAsPieChartProps)
                      .winPercent
                : undefined;
        const secondaryValue =
            'secondaryValue' in props
                ? (props as TradingPlatformStatisticCardAsValueProps)
                      .secondaryValue
                : undefined;
        const secondaryLabel =
            'secondaryLabel' in props
                ? (props as TradingPlatformStatisticCardAsValueProps)
                      .secondaryLabel
                : undefined;

        const { t } = useTranslation('trading-journal');

        const currency = useGetTradingPlatformSessionCurrency();

        const isValueEmpty = useMemo(() => {
            if (winPercent != null) {
                return false;
            }

            return value === undefined || value === null || value === '';
        }, [value, winPercent]);

        const currentValue = useMemo(() => {
            if (winPercent != null) {
                return `${winPercent.toFixed(2)}%`;
            }

            if (isValueEmpty) {
                return '--';
            }

            if (!hasPrefix) {
                return value;
            }

            const numericValue = Number(value);

            if (Number.isNaN(numericValue)) {
                return '--';
            }

            return parseNegativeNumber(numericValue, currency);
        }, [currency, hasPrefix, isValueEmpty, value, winPercent]);

        const colorize = useMemo(() => {
            if (isValueEmpty || !hasColor) {
                return preferredColor;
            }

            let numericValue = Number(value);

            if (Number.isNaN(numericValue) && typeof value === 'string') {
                const cleanedValue = value.replace(/[^0-9.-]/g, '');

                numericValue = Number(cleanedValue);
            }

            if (Number.isNaN(numericValue)) {
                return preferredColor;
            }

            return preferredColor || (numericValue >= 0 ? 'up' : 'down');
        }, [hasColor, isValueEmpty, preferredColor, value]);

        const contextClass = useMemo(() => {
            if (colorize === 'up') {
                return cls.positiveContext;
            }

            if (colorize === 'down') {
                return cls.negativeContext;
            }

            return cls.neutralContext;
        }, [colorize]);

        const getMetricValue = useCallback(
            (metric: MetricItem, isEmpty: boolean) => {
                if (isEmpty) {
                    return '--';
                }

                if (!metric.hasPrefix) {
                    return metric.value;
                }

                const numericValue = Number(metric.value);

                if (Number.isNaN(numericValue)) {
                    return '--';
                }

                return parseNegativeNumber(numericValue, currency);
            },
            [currency],
        );

        const getMetricColorize = useCallback(
            (metric: MetricItem, isEmpty: boolean) => {
                if (isEmpty || !metric.hasColor) {
                    return metric.preferredColor;
                }

                const numericValue = Number(metric.value);

                if (Number.isNaN(numericValue)) {
                    return metric.preferredColor;
                }

                return (
                    metric.preferredColor || (numericValue >= 0 ? 'up' : 'down')
                );
            },
            [],
        );

        const renderMetric = useCallback(
            (metric: MetricItem) => {
                const isMetricEmpty =
                    metric.value === undefined ||
                    metric.value === null ||
                    metric.value === '';

                const metricCurrentValue = getMetricValue(
                    metric,
                    isMetricEmpty,
                );
                const metricColorize = getMetricColorize(metric, isMetricEmpty);

                return (
                    <Flex
                        key={metric.titleKey}
                        gap={4}
                        vertical
                    >
                        <Typography.Text className={cls.title}>
                            {t(metric.titleKey)}
                        </Typography.Text>
                        {isLoading ? (
                            <Skeleton.Input
                                active
                                className={cls.valueSkeleton}
                            />
                        ) : (
                            <Typography.Text
                                className={cn(cls.value, {
                                    [cls.down]:
                                        !isMetricEmpty &&
                                        metricColorize === 'down',
                                    [cls.empty]: isMetricEmpty,
                                    [cls.up]:
                                        !isMetricEmpty &&
                                        metricColorize === 'up',
                                })}
                            >
                                {metricCurrentValue}
                            </Typography.Text>
                        )}
                    </Flex>
                );
            },
            [getMetricColorize, getMetricValue, isLoading, t],
        );

        const hasMetrics = metrics && metrics.length > 0;

        const renderMetricRingsSkeleton = () => (
            <div className={cls.metricRingsSkeleton}>
                <Flex
                    className={cls.metricRingsSkeletonText}
                    gap={4}
                    vertical
                >
                    <Skeleton.Input
                        active
                        size="small"
                        style={{ width: 80 }}
                    />
                    <Skeleton.Input
                        active
                        style={{ width: 60, height: 24 }}
                    />
                </Flex>
                <div className={cls.metricRingsSkeletonRing}>
                    <Skeleton.Avatar
                        active
                        size={64}
                    />
                </div>
                <Flex
                    className={cls.metricRingsSkeletonText}
                    gap={4}
                    vertical
                >
                    <Skeleton.Input
                        active
                        size="small"
                        style={{ width: 80 }}
                    />
                    <Skeleton.Input
                        active
                        style={{ width: 60, height: 24 }}
                    />
                </Flex>
                <div className={cls.metricRingsSkeletonRing}>
                    <Skeleton.Avatar
                        active
                        size={64}
                    />
                </div>
            </div>
        );

        const renderRatioBarSkeleton = () => (
            <Flex
                gap={16}
                vertical
            >
                <Flex justify="space-between">
                    <Flex
                        gap={4}
                        vertical
                    >
                        <Skeleton.Input
                            active
                            size="small"
                            style={{ width: 100 }}
                        />
                        <Skeleton.Input
                            active
                            style={{ width: 70, height: 24 }}
                        />
                    </Flex>
                    <Flex
                        align="flex-end"
                        gap={4}
                        vertical
                    >
                        <Skeleton.Input
                            active
                            size="small"
                            style={{ width: 100 }}
                        />
                        <Skeleton.Input
                            active
                            style={{ width: 70, height: 24 }}
                        />
                    </Flex>
                </Flex>
                <Skeleton.Button
                    active
                    block
                    style={{ height: 24, borderRadius: 12 }}
                />
            </Flex>
        );

        const renderVisualIndicatorSkeleton = () => (
            <div className={cls.visualIndicatorSkeleton}>
                {skeletonVariant === 'ratio-bar'
                    ? renderRatioBarSkeleton()
                    : renderMetricRingsSkeleton()}
            </div>
        );

        const renderMultiMetrics = () => (
            <Flex
                className={cn(cls.multiMetricsContainer, {
                    [cls.fullWidthVisualIndicator]: !hasMetrics,
                })}
                gap={16}
            >
                {hasMetrics && (
                    <Flex
                        className={cls.metricsColumn}
                        gap={16}
                        vertical
                    >
                        {metrics?.map(renderMetric)}
                    </Flex>
                )}
                {visualIndicator && (
                    <div className={cls.visualIndicator}>
                        {isLoading
                            ? renderVisualIndicatorSkeleton()
                            : visualIndicator}
                    </div>
                )}
            </Flex>
        );

        // Check if this card expects a percentage value
        const hasPercentage = 'percentage' in props;

        const renderSecondaryValue = () => {
            if (secondaryValue === undefined) {
                return null;
            }

            if (isLoading) {
                return (
                    <Skeleton.Input
                        active
                        className={cls.secondarySkeleton}
                    />
                );
            }

            return (
                <Flex
                    className={cls.secondaryBlock}
                    vertical
                >
                    <Typography.Text className={cls.secondaryValue}>
                        {secondaryValue}
                    </Typography.Text>
                    {secondaryLabel && (
                        <Typography.Text className={cls.secondaryLabel}>
                            {secondaryLabel}
                        </Typography.Text>
                    )}
                </Flex>
            );
        };

        const renderSingleValue = () => (
            <Flex
                align="center"
                className={cls.valueRow}
                justify="space-between"
            >
                <Flex
                    align="center"
                    className={cls.valueRowInner}
                    justify="space-between"
                >
                    {isLoading ? (
                        <>
                            <Skeleton.Input
                                active
                                className={cls.valueSkeleton}
                            />
                            {hasPercentage && (
                                <Skeleton.Input
                                    active
                                    className={cls.percentageSkeleton}
                                />
                            )}
                        </>
                    ) : (
                        <>
                            <Typography.Text
                                className={cn(cls.value, {
                                    [cls.down]:
                                        !isValueEmpty && colorize === 'down',
                                    [cls.empty]: isValueEmpty,
                                    [cls.up]:
                                        !isValueEmpty && colorize === 'up',
                                })}
                            >
                                {currentValue}
                            </Typography.Text>
                            {percentage !== undefined && (
                                <Typography.Text
                                    className={cn(cls.value, {
                                        [cls.down]:
                                            !isValueEmpty &&
                                            colorize === 'down',
                                        [cls.empty]:
                                            isValueEmpty ||
                                            percentage === '--%',
                                        [cls.up]:
                                            !isValueEmpty && colorize === 'up',
                                    })}
                                >
                                    {percentage}
                                </Typography.Text>
                            )}
                        </>
                    )}
                </Flex>
                {winPercent != null &&
                    (isLoading ? (
                        <Skeleton.Avatar
                            active
                            size={56}
                        />
                    ) : (
                        <TradingPlatformStatisticPieChart
                            titleKey={titleKey}
                            winLoss={winLoss}
                            winPercent={winPercent}
                        />
                    ))}
            </Flex>
        );

        // Determine if this card should show a sparkline
        // Show if: sparklineData was passed AND (loading OR has valid data)
        const hasSparklineData = sparklineData !== undefined;
        const hasValidSparklineData =
            sparklineData && sparklineData.length >= 2;
        const shouldShowSparkline =
            hasSparklineData && (isLoading || hasValidSparklineData);

        return (
            <Card
                helpId={helpId}
                isFullHeight
                subtitle={subtitle}
                title={t(titleKey)}
                wrapperClassName={cn(cls.root, contextClass, {
                    [cls.hasSparkline]: shouldShowSparkline,
                })}
            >
                {metrics ? renderMultiMetrics() : renderSingleValue()}
                {!metrics && renderSecondaryValue()}
                {shouldShowSparkline && (
                    <div className={cls.sparklineContainer}>
                        <Sparkline
                            color={sparklineColor}
                            data={sparklineData}
                            isLoading={isLoading}
                        />
                    </div>
                )}
            </Card>
        );
    },
);

TradingPlatformStatisticCard.displayName = 'TradingPlatformStatisticCard';
